import React, { useState } from 'react';
import { QuestionFull as Neutral, X } from '@fabric-ui/icons';
import { ErrorFull as Error, SuccessFull as Success, WarningFullOrange as Warning } from '@fabric-ui/icons/colorful';
import { flex } from '@fabric-ui/theme';
import { Box, BoxProps, Collapse, IconButton, SxProps, useTheme } from '@mui/material';
import { ExpandCard } from './ExpandCard';

export interface Props {
  variant: AttentionBoxVariant;
  content: string | React.ReactElement;
  collapseContent?: string | React.ReactElement;
  showIcon?: boolean;
  small?: boolean;
  onClick?: () => void;
  onDismiss?: () => void;
  icon?: React.JSX.Element;
  sx?: SxProps;
}

export const AvAttentionBox: React.FC<Props> = ({
  variant,
  content,
  showIcon = true,
  small = false,
  onClick,
  collapseContent,
  onDismiss,
  icon,
  sx,
}) => {
  const { palette } = useTheme();
  const [open, setOpen] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const bgVariant = {
    success: palette.colors.positive[100],
    error: palette.colors.negative[150],
    neutral: palette.colors.neutrals[150],
    warning: palette.colors.orange[100],
  };

  const iconVariant = {
    success: <Success />,
    error: <Error />,
    neutral: <Neutral />,
    warning: <Warning />,
  };

  return isDismissed ? null : (
    <Box
      onClick={() => {
        onClick?.();
      }}
      sx={{
        cursor: onClick ? 'pointer' : 'inherit',
        py: small ? 1 : '10px',
        px: 2,
        background: bgVariant[variant],
        ...(flex.col as any),
        borderRadius: 2,
        ...sx,
      }}>
      <Box sx={{ ...flex.itemsStart, gap: 1 }}>
        {showIcon && (icon || iconVariant[variant])}
        <Box sx={{ flex: 1 }}>{content}</Box>
        {collapseContent && <ExpandCard expand={open} onClick={() => setOpen(!open)} />}
        {onDismiss && (
          <IconButton
            onClick={() => {
              onDismiss();
              setIsDismissed(true);
            }}>
            <X />
          </IconButton>
        )}
      </Box>
      {collapseContent ? <Collapse in={open}>{collapseContent}</Collapse> : null}
    </Box>
  );
};

export enum AttentionBoxVariant {
  Warning = 'warning',
  Error = 'error',
  Success = 'success',
  Neutral = 'neutral',
}
